import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, Ticket, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase, supabaseConfigured } from "@/lib/supabase";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!supabaseConfigured) {
      setError("Supabase is niet geconfigureerd.");
      return;
    }
    // Supabase places the recovery access_token in the URL hash and consumes it via
    // detectSessionInUrl (default true). We just need to wait until the recovery event fires.
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (mounted && data.session) setReady(true);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (session && !ready)) setReady(true);
    });
    // Give supabase-js a beat to parse the hash on cold loads
    const t = setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      if (mounted && !ready && !data.session) {
        setError("Ongeldige of verlopen reset-link. Vraag een nieuwe aan.");
      }
    }, 1200);
    return () => {
      mounted = false;
      clearTimeout(t);
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Minimaal 8 tekens");
    if (password !== confirm) return toast.error("Wachtwoorden komen niet overeen");
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      toast.success("Wachtwoord aangepast");
      // Sign out any lingering recovery session so the user has to log in cleanly.
      await supabase.auth.signOut();
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Kon wachtwoord niet aanpassen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-reset-password-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple">
            <Ticket className="h-5 w-5 text-white" />
          </span>
          <span className="text-white font-extrabold tracking-tight text-xl">
            StudioVoorJou<span className="text-svj-cyan"> Beheer</span>
          </span>
        </Link>

        <div className="bg-svj-card border border-svj-border rounded-2xl p-8">
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
            Nieuw wachtwoord
          </div>
          <h1 className="mt-1 text-2xl font-extrabold text-white">Kies een wachtwoord</h1>
          <p className="mt-2 text-sm text-slate-400">
            Minimaal 8 tekens. Gebruik iets dat je zelf niet kunt raden op donderdag.
          </p>

          {error && (
            <div data-testid="reset-password-error" className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
              {error}
            </div>
          )}

          {!error && !ready && (
            <div className="mt-8 py-8 text-center text-slate-400">
              <Loader2 className="h-6 w-6 mx-auto animate-spin text-svj-cyan" />
              <div className="mt-3 text-xs">Reset-link controleren…</div>
            </div>
          )}

          {!error && ready && (
            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <label className="block" htmlFor="reset-password">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Nieuw wachtwoord
                </span>
                <input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-new"
                  className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
                />
              </label>
              <label className="block" htmlFor="reset-password-confirm">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Bevestig wachtwoord
                </span>
                <input
                  id="reset-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="reset-password-submit"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                  submitting
                    ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
                }`}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <KeyRound className="h-5 w-5" />}
                {submitting ? "Bezig…" : "Wachtwoord opslaan"}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/admin/login" data-testid="reset-password-back" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5" /> Terug naar inloggen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
